export const HANDYOUT_SECTION_KEYS = ["intro", "toc", "key-takeaways", "faq", "feedback"] as const;

export type HandyoutSectionKey = (typeof HANDYOUT_SECTION_KEYS)[number];

export type ModuleSection = HandyoutSectionKey | "slide";

export type StoredModule = Record<string, unknown> & {
  id: string;
  type: string;
  version: number;
  hidden?: boolean;
};

export interface HandyoutSlideRecord {
  id: string;
  handyout_id: string;
  slide_number: number;
  source_slide_number?: number | null;
  details?: string | null;
  slide_context?: unknown;
  modules: StoredModule[];
  [key: string]: unknown;
}

export type HandyoutSections = Partial<Record<HandyoutSectionKey, StoredModule>>;

export interface ModuleHistoryState {
  can_undo: boolean;
  can_redo: boolean;
}

export interface ModuleSaveRequest {
  handyout_id: string;
  slides?: Record<string, StoredModule[]>;
  sections?: HandyoutSections;
  deleted?: { id: string; version: number }[];
}

export interface ModuleSaveResponse {
  batch_id: string | null;
  modules: StoredModule[];
  deleted: string[];
  history: ModuleHistoryState;
}

export interface ModuleStateResponse {
  batch_id: string | null;
  slides: HandyoutSlideRecord[];
  sections: HandyoutSections;
  history: ModuleHistoryState;
}

export interface ModuleConflict {
  module_id: string;
  current: StoredModule | null;
}

export type ModuleChangeType = "insert" | "upsert" | "reorder" | "delete" | "slide_order";

export type ModuleRevisionSource =
  | "editor"
  | "ai_accept"
  | "ai_fill"
  | "pipeline"
  | "migration"
  | "restore"
  | "undo"
  | "redo";

export interface ModuleRevisionChange {
  module_id: string | null;
  change_type: ModuleChangeType;
  type: string | null;
  title: string | null;
  slide_id: string | null;
  section: ModuleSection | null;
}

export interface ModuleRevisionBatch {
  batch_id: string;
  created_at: string;
  updated_at: string;
  source: ModuleRevisionSource;
  actor_user_id: string | null;
  module_message_id: string | null;
  reverts_batch_id: string | null;
  changes: ModuleRevisionChange[];
}

export interface ModuleRevisionsResponse {
  batches: ModuleRevisionBatch[];
  can_undo: boolean;
  can_redo: boolean;
}

export interface SlideReorderResponse {
  batch_id: string | null;
  order: string[];
  history: ModuleHistoryState;
}

export type HandyoutState = "unpublished" | "published";

export type PublishStatus = "draft" | "offline" | "live" | "expired";

export interface HandyoutPublishState {
  state: HandyoutState;
  publish_status: PublishStatus;
  has_unpublished_changes: boolean;
  last_published_at: string | null;
  expires_at: string | null;
}

export interface PublishResult {
  version_id: string;
  version: number;
  created: boolean;
  state_changed: boolean;
  previous_version_id: string | null;
  handyout: HandyoutPublishState;
}

export interface UnpublishResult {
  handyout: HandyoutPublishState;
}

export interface PublishedVersionInfo {
  number: number;
  published_at: string | null;
}

export type PublishedSlideRecord = Pick<
  HandyoutSlideRecord,
  "id" | "slide_number" | "source_slide_number" | "details"
> & {
  modules: StoredModule[];
  [key: string]: unknown;
};

export interface PublishedVersion {
  version: PublishedVersionInfo;
  slides: PublishedSlideRecord[];
  sections: HandyoutSections;
}

export interface PublicHandyoutProfile {
  avatar_url?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
  feedback_thank_you_message?: string | null;
  [key: string]: unknown;
}

export interface PublicHandyoutExpired {
  status: "expired";
  title: string | null;
  expires_at: string | null;
}

export interface PublicHandyoutLive {
  status: "live";
  id: string;
  presentation_id: string;
  title: string | null;
  expires_at: string | null;
  primary_color: string | null;
  palette_scheme: string | null;
  presentations: { summary: string | null } | null;
  profile: PublicHandyoutProfile | null;
  version: PublishedVersionInfo;
  slides: PublishedSlideRecord[];
  sections: HandyoutSections;
  source_language: string | null;
  language: string | null;
  languages: string[];
}

export type PublicHandyoutPayload = PublicHandyoutLive | PublicHandyoutExpired | null;
